import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {ZadCustomerReturn} from '../../../models/zad'
import {ZadApiError} from '../zad/errors'
import {
  cancelZadCustomerReturn,
  createZadCustomerReturn,
  fetchZadCustomerReturnById,
  fetchZadCustomerReturnPickerCustomers,
  fetchZadCustomerReturnPickerSales,
  fetchZadCustomerReturns,
  fetchZadCustomerReturnSaleById,
  fetchZadSaleReturnable,
  updateZadCustomerReturn,
  validateZadCustomerReturn,
} from './service'
import {FetchZadCustomerReturnsResponse, ZadCustomerReturnState} from './types'

const initialState: ZadCustomerReturnState = {
  returns: [],
  total: 0,
  totalPages: 0,
  listLoading: false,
  listInitialized: false,
  actionLoading: false,
  selectedReturn: null,
  returnable: null,
  pickerSales: [],
  pickerCustomers: [],
  pickerLoading: false,
  error: null,
  errorCode: null,
  errorStatus: null,
}

function applyRejected(state: ZadCustomerReturnState, action: PayloadAction<any>) {
  const payload = (action.payload || {}) as ZadApiError
  state.error = payload.error || 'Request failed'
  state.errorCode = payload.errorCode || null
  state.errorStatus = payload.status || null
}

function clearError(state: ZadCustomerReturnState) {
  state.error = null
  state.errorCode = null
  state.errorStatus = null
}

function upsertReturn(state: ZadCustomerReturnState, customerReturn?: ZadCustomerReturn | null) {
  if (!customerReturn?._id) return
  state.selectedReturn = customerReturn
  const index = state.returns.findIndex((item) => item._id === customerReturn._id)
  if (index >= 0) {
    state.returns[index] = customerReturn
  }
}

const zadCustomerReturnSlice = createSlice({
  name: 'zadCustomerReturns',
  initialState,
  reducers: {
    clearZadCustomerReturnError: (state) => {
      clearError(state)
    },
    clearSelectedZadCustomerReturn: (state) => {
      state.selectedReturn = null
    },
    clearZadSaleReturnable: (state) => {
      state.returnable = null
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchZadCustomerReturns.pending, (state) => {
      state.listLoading = true
      clearError(state)
    })
    builder.addCase(fetchZadCustomerReturns.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.listLoading = false
      state.listInitialized = true
    })
    builder.addCase(
      fetchZadCustomerReturns.fulfilled,
      (state, action: PayloadAction<FetchZadCustomerReturnsResponse>) => {
        state.listLoading = false
        state.listInitialized = true
        clearError(state)
        state.returns = action.payload.returns
        state.total = action.payload.total
        state.totalPages = action.payload.totalPages
      }
    )

    builder.addCase(fetchZadCustomerReturnById.pending, (state) => {
      state.actionLoading = true
    })
    builder.addCase(fetchZadCustomerReturnById.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(fetchZadCustomerReturnById.fulfilled, (state, action) => {
      state.actionLoading = false
      state.selectedReturn = action.payload.customerReturn
    })

    builder.addCase(fetchZadSaleReturnable.fulfilled, (state, action) => {
      state.returnable = action.payload.returnable
    })
    builder.addCase(fetchZadSaleReturnable.rejected, (state) => {
      state.returnable = null
    })

    builder.addCase(createZadCustomerReturn.pending, (state) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(createZadCustomerReturn.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(createZadCustomerReturn.fulfilled, (state, action) => {
      state.actionLoading = false
      clearError(state)
      upsertReturn(state, action.payload.customerReturn)
    })

    builder.addCase(updateZadCustomerReturn.pending, (state) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(updateZadCustomerReturn.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(updateZadCustomerReturn.fulfilled, (state, action) => {
      state.actionLoading = false
      clearError(state)
      upsertReturn(state, action.payload.customerReturn)
    })

    builder.addCase(validateZadCustomerReturn.pending, (state) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(validateZadCustomerReturn.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(validateZadCustomerReturn.fulfilled, (state, action) => {
      state.actionLoading = false
      clearError(state)
      upsertReturn(state, action.payload.customerReturn)
    })

    builder.addCase(cancelZadCustomerReturn.pending, (state) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(cancelZadCustomerReturn.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(cancelZadCustomerReturn.fulfilled, (state, action) => {
      state.actionLoading = false
      clearError(state)
      upsertReturn(state, action.payload.customerReturn)
    })

    builder.addCase(fetchZadCustomerReturnPickerSales.pending, (state) => {
      state.pickerLoading = true
    })
    builder.addCase(fetchZadCustomerReturnPickerSales.rejected, (state) => {
      state.pickerLoading = false
    })
    builder.addCase(fetchZadCustomerReturnPickerSales.fulfilled, (state, action) => {
      state.pickerLoading = false
      state.pickerSales = action.payload.sales
    })
    builder.addCase(fetchZadCustomerReturnSaleById.fulfilled, (state, action) => {
      if (!action.payload.sale?._id) return
      const exists = state.pickerSales.some((sale) => sale._id === action.payload.sale._id)
      if (!exists) {
        state.pickerSales = [action.payload.sale, ...state.pickerSales]
      }
    })

    builder.addCase(fetchZadCustomerReturnPickerCustomers.pending, (state) => {
      state.pickerLoading = true
    })
    builder.addCase(fetchZadCustomerReturnPickerCustomers.rejected, (state) => {
      state.pickerLoading = false
    })
    builder.addCase(fetchZadCustomerReturnPickerCustomers.fulfilled, (state, action) => {
      state.pickerLoading = false
      state.pickerCustomers = action.payload.customers
    })
  },
})

export default zadCustomerReturnSlice.reducer
export const actions = zadCustomerReturnSlice.actions
export {initialState as zadCustomerReturnInitialState}
